// src/pages/SinhBoSoQr/KyPhatHanhList.tsx
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
} from 'reactstrap';
import CircularProgress from 'Components/Common/CircularProgress';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import {
  buildJobPanelKey,
  JOB_PANEL_TOGGLE_REQUEST_EVENT,
  JOB_PANEL_STATE_EVENT,
} from 'helpers/activeExportJobsStorage';
import {
  KyPhatHanh,
  LOAI_VE_MAU_LABELS,
  QrGenerationJob,
  SinhBoSoQrFilters,
  SinhBoSoQrPagination,
  TRANG_THAI_QR_LABELS,
} from './types';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('sinh_bo_so_qr');

// Tap action-code tinh cua cot "Thao tac" o bang ky phat hanh — dung de an han ca cot khi role
// hien tai khong co quyen voi bat ky action nao trong cot (khong phu thuoc trang thai dong).
const KY_ACTION_COLUMN_CODES = ['button-generate', 'button-cancel-qr-job'];

// Tim job sinh bo so & QR dang active (chua ket thuc) cua 1 ky phat hanh trong danh sach activeJobs
// (Redux, do GlobalJobProgress polling va cap nhat) — dung chung cho ca TrangThaiQRCell (hien
// progress) va ActionButtons (nut Huy), tranh viet trung logic tim kiem.
const findActiveJob = (ky: KyPhatHanh, activeJobs: QrGenerationJob[]) =>
  activeJobs.find((job) => {
    if (job.lotteryPeriodId !== ky.id) return false;
    const status = String(job.status || '').toLowerCase();
    return status !== 'completed' && status !== 'failed' && status !== 'stalled';
  });

interface KyPhatHanhListProps {
  data: KyPhatHanh[];
  activeJobs: QrGenerationJob[];
  loading: boolean;
  pagination: SinhBoSoQrPagination;
  filters: SinhBoSoQrFilters;
  onSinh: (ky: KyPhatHanh) => void;
  onCancelJob: (ky: KyPhatHanh, jobId: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFilterChange: (filterName: string, value: any) => void;
  onClearFilters: () => void;
}

const TrangThaiQRBadge: React.FC<{ trangThai: string }> = ({ trangThai }) => {
  const config = {
    Pending: { color: 'warning', label: TRANG_THAI_QR_LABELS.Pending },
    Generated: { color: 'success', label: TRANG_THAI_QR_LABELS.Generated },
    DangChoSinh: { color: 'warning', label: TRANG_THAI_QR_LABELS.DangChoSinh },
    DaSinhBoSoVaQR: { color: 'success', label: TRANG_THAI_QR_LABELS.DaSinhBoSoVaQR },
  }[trangThai] ?? { color: 'secondary', label: trangThai };

  return (
    <span className={`badge bg-${config.color}-subtle text-${config.color} text-uppercase`}>
      {config.label}
    </span>
  );
};

// Hien thi progress dang vong tron (%) khi ky dang co job sinh vé/QR chay nen — dung chung
// component CircularProgress voi cot trang thai o man "Kế hoạch phát hành" (PlanStatusColumn.tsx)
// de 2 man hien thi giong het nhau. Job duoc GlobalJobProgress (mount toan cuc o Layout) polling
// va cap nhat lien tuc vao Redux activeJobs, nen o day chi can doc lai, khong can tu poll rieng.
const TrangThaiQRCell: React.FC<{ ky: KyPhatHanh; activeJobs: QrGenerationJob[] }> = ({ ky, activeJobs }) => {
  const activeJob = findActiveJob(ky, activeJobs);

  // Key cua popup noi (GlobalJobProgress.tsx) tuong ung job dang chay — dung de dong bo icon
  // "eye" voi dung trang thai thu gon/mo rong cua popup do, y het PlanStatusColumn.tsx o man
  // "Kế hoạch phát hành".
  const activePanelKey = activeJob ? buildJobPanelKey('qr', activeJob.jobId) : null;

  // Mac dinh coi la dang mo rong (khop hanh vi "tu dong mo popup khi job vua xuat hien lan dau"
  // cua GlobalJobProgress) — se duoc GlobalJobProgress phat lai dung trang thai ngay sau khi
  // component nay mount, qua JOB_PANEL_STATE_EVENT.
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  useEffect(() => {
    if (!activePanelKey) return undefined;

    const handlePanelState = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key !== activePanelKey) return;
      setPanelCollapsed(!!detail.collapsed);
    };

    window.addEventListener(JOB_PANEL_STATE_EVENT, handlePanelState);
    return () => window.removeEventListener(JOB_PANEL_STATE_EVENT, handlePanelState);
  }, [activePanelKey]);

  const handleTogglePanel = () => {
    if (!activePanelKey) return;
    window.dispatchEvent(new CustomEvent(JOB_PANEL_TOGGLE_REQUEST_EVENT, { detail: { key: activePanelKey } }));
  };

  if (activeJob) {
    return (
      <div className="d-flex align-items-center">
        <CircularProgress percent={activeJob.progressPercent} label={st('dang_sinh_ve_qr_label')} />
        <button
          type="button"
          className="btn btn-sm btn-light p-1 lh-1 ms-2"
          aria-label={panelCollapsed ? st('mo_rong_popup_tien_do_text') : st('thu_nho_popup_tien_do_text')}
          title={panelCollapsed ? st('mo_rong_popup_tien_do_text') : st('thu_nho_popup_tien_do_text')}
          onClick={handleTogglePanel}
        >
          <i className={panelCollapsed ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
        </button>
      </div>
    );
  }

  return <TrangThaiQRBadge trangThai={ky.qrStatus} />;
};

const ActionButtons: React.FC<{
  ky: KyPhatHanh;
  activeJobs: QrGenerationJob[];
  onSinh: (ky: KyPhatHanh) => void;
  onCancelJob: (ky: KyPhatHanh, jobId: string) => void;
}> = ({ ky, activeJobs, onSinh, onCancelJob }) => {
  const location = useLocation();

  if (ky.qrStatus === 'DangChoSinh') {
    const activeJob = findActiveJob(ky, activeJobs);
    if (!activeJob) {
      // Truong hop hiem: job vua ket thuc nhung danh sach chua kip fetch lai — giu nut cho o
      // trang thai vo hieu hoa thay vi hien nut Huy tro tren khong co jobId de goi.
      return (
        <Button color="light" size="sm" className="btn-icon" title={st('dang_sinh_bo_so_qr_vui_title')} disabled>
          <i className="ri-loader-4-line text-primary"></i>
        </Button>
      );
    }
    if (!PageActionPermission.canDisplay(location.pathname, 'button-cancel-qr-job')) {
      return (
        <Button color="light" size="sm" className="btn-icon" title={st('dang_sinh_bo_so_qr_vui_title')} disabled>
          <i className="ri-loader-4-line text-primary"></i>
        </Button>
      );
    }
    return (
      <Button
        color="danger"
        outline
        size="sm"
        className="btn-icon"
        title={st('huy_sinh_bo_so_qr_text')}
        data-action-code="button-cancel-qr-job"
        data-action-name="Hủy sinh bộ số & QR"
        onClick={() => onCancelJob(ky, activeJob.jobId)}
      >
        <i className="ri-close-circle-line"></i>
      </Button>
    );
  }

  if (ky.qrStatus !== 'Pending') {
    return (
      <Button color="light" size="sm" className="btn-icon" title={st('da_xu_ly_title')} disabled>
        <i className="ri-lock-line text-muted"></i>
      </Button>
    );
  }

  if (!PageActionPermission.canDisplay(location.pathname, 'button-generate')) {
    return null;
  }

  return (
    <Button
      color="primary"
      outline
      size="sm"
      className="btn-icon"
      title={st('sinh_bo_so_qr_title')}
      data-action-code="button-generate"
      data-action-name="Sinh bộ số & QR"
      onClick={() => onSinh(ky)}
    >
      <i className="ri-qr-code-line"></i>
    </Button>
  );
};

const KyPhatHanhList: React.FC<KyPhatHanhListProps> = ({
  data,
  activeJobs,
  loading,
  pagination,
  filters,
  onSinh,
  onCancelJob,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onClearFilters,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    KY_ACTION_COLUMN_CODES,
  );

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return moment(date).format('DD/MM/YYYY');
  };

  // Local state cho ô tìm kiếm — chỉ gọi onFilterChange (kích hoạt gọi API) sau 500ms ngừng gõ
  const [searchValue, setSearchValue] = useState(filters.searchTerm || '');

  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      onFilterChange('searchTerm', searchValue);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (filters.searchTerm === '') {
      setSearchValue('');
    }
  }, [filters.searchTerm]);

  return (
    <Card id="qrGenerationList">
      <CardHeader className="card-header border-0">
        <Row className="align-items-center gy-3">
          <div className="col-sm">
            <h5 className="card-title mb-0">{st('danh_sach_ky_phat_hanh_title')}</h5>
          </div>
          <div className="col-sm-auto">
            <Button color="soft-secondary" disabled={loading} onClick={onClearFilters}>
              <i className="ri-refresh-line align-bottom me-1"></i>
              {st('xoa_loc_button')}
            </Button>
          </div>
        </Row>
      </CardHeader>

      <CardBody className="pt-0">
        <Row className="g-3 mb-3 align-items-end mb-5">
          <Col md={4}>
            <Label className="form-label">{st('tim_kiem_label')}</Label>
            <Input
              type="text"
              placeholder={st('tim_theo_ten_ky_loai_ve_placeholder')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">{st('trang_thai_qr_label')}</Label>
            <Input
              type="select"
              className="form-select"
              value={filters.trangThaiQR}
              onChange={(event) => onFilterChange('trangThaiQR', event.target.value)}
            >
              <option value="All">{st('tat_ca_option')}</option>
              <option value="Pending">{st('cho_sinh_option')}</option>
              <option value="DangChoSinh">{st('dang_sinh_option')}</option>
              <option value="Generated">{st('da_sinh_qr_option')}</option>
            </Input>
          </Col>
          <Col md={5} className="text-md-end">
            <span className="text-muted">
              {st('tong_text')} <span className="fw-semibold">{Number(pagination.totalItems || 0).toLocaleString('vi-VN')}</span> {st('ban_ghi_text')}
            </span>
          </Col>
        </Row>

        <DataTableShell
          headerRow={
            <tr>
              <th className="text-center">{st('stt_column')}</th>
              <th>{st('ky_phat_hanh_column')}</th>
              <th>{st('loai_ve_mau_column')}</th>
              <th>{st('so_luong_column')}</th>
              <th>{st('ngay_su_dung_column')}</th>
              <th>{st('trang_thai_qr_label')}</th>
              {canShowActionColumn && (
                <th style={{ width: 120 }} className="text-center">{st('thao_tac_column')}</th>
              )}
            </tr>
          }
        >
              {loading ? (
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    {Array.from({ length: canShowActionColumn ? 7 : 6 }).map((__, col) => (
                      <td key={col}>
                        <div className="placeholder-glow">
                          <span className="placeholder col-8"></span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={canShowActionColumn ? 7 : 6} className="text-center text-muted py-5">
                    <div className="mb-2">
                      <i className="ri-file-list-3-line fs-1 text-muted"></i>
                    </div>
                    {st('khong_co_du_lieu_text')}
                  </td>
                </tr>
              ) : (
                data.map((ky, index) => (
                  <tr key={ky.id}>
                    <td className="text-center">
                      {getSttNumber(pagination.currentPage, pagination.pageSize, index)}
                    </td>
                    <td className="fw-semibold">{ky.periodName}</td>
                    <td>{LOAI_VE_MAU_LABELS[ky.ticketType] || ky.ticketType}</td>
                    <td>{ky.ticketQuantity.toLocaleString('vi-VN')}</td>
                    <td>{formatDate(ky.useDate)}</td>
                    <td>
                      <TrangThaiQRCell ky={ky} activeJobs={activeJobs} />
                    </td>
                    {canShowActionColumn && (
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1 flex-nowrap">
                          <ActionButtons ky={ky} activeJobs={activeJobs} onSinh={onSinh} onCancelJob={onCancelJob} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
        </DataTableShell>

        {!loading && data.length > 0 && (
          <Row className="mt-3 align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Label className="mb-0">{st('hien_thi_label')}</Label>
                <Input
                  type="select"
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={pagination.pageSize}
                  onChange={(event) => onPageSizeChange(Number(event.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
                <span className="text-muted">
                  {st('trang_text')} {pagination.currentPage}{pagination.totalPages ? ` / ${pagination.totalPages}` : ''}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end">
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => onPageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                      >
                        {st('truoc_button')}
                      </button>
                    </li>
                    {Array.from({ length: pagination.totalPages }).map((_, index) => (
                      <li
                        key={index + 1}
                        className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button className="page-link" onClick={() => onPageChange(index + 1)}>
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => onPageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                      >
                        {st('sau_button')}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default KyPhatHanhList;
